import React, { createContext, useRef, useCallback } from 'react'
import { useReducer } from 'react'
import { updateFields, updatePreviewHtml } from './pages/preview'
import { savePage } from './pages/save'
import { publishPage, saveForPublishing, unpublishPage } from './pages/publish'

const store = createContext({})

const { Provider } = store

const actions = {
  TOGGLE_THEME_EDITOR: 'TOGGLE_THEME_EDITOR',
  TOGGLE_PAGE_EDITOR: 'TOGGLE_PAGE_EDITOR',
  REFRESH_IFRAME: 'REFRESH_IFRAME',
  FINISHED_REFRESHING_IFRAME: 'FINISHED_REFRESHING_IFRAME',
  UPDATE_HTML: 'UPDATE_HTML',
  SET_HTML: 'SET_HTML',
  SET_FORM_REF: 'SET_FORM_REF',
  UPDATE_DEV_MODE: 'UPDATE_DEV_MODE',
  UPDATE_DEV_MODE: 'UPDATE_DEV_MODE',
  SAVE_PAGE: 'SAVE_PAGE',
  SET_SAVING: 'SET_SAVING',
  SET_IFRAME_REF: 'SET_IFRAME_REF',
  SHOW_CONTENT_EDITOR: 'SHOW_CONTENT_EDITOR',
  SHOW_PREVIEW: 'SHOW_PREVIEW',
  SHOW_THEME_EDITOR: 'SHOW_THEME_EDITOR',
  SET_BLOCKS: 'SET_BLOCKS',
  GET_BLOCKS_DATA: 'GET_BLOCKS_DATA',
  START_PUBLISHING: 'START_PUBLISHING',
  SET_PUBLISHING: 'SET_PUBLISHING',
  PUBLISH_PAGE: 'PUBLISH_PAGE',
  SET_PAGE: 'SET_PAGE',
  SET_PUBLISH_URLS: 'SET_PUBLISH_URLS',
  SET_UNPUBLISHING: 'SET_UNPUBLISHING',
  UNPUBLISH_PAGE: 'UNPUBLISH_PAGE',
  UPDATE_FIELD: 'UPDATE_FIELD',
  TOGGLE_MOBILE_SIDEBAR: 'TOGGLE_MOBILE_SIDEBAR',
}

const useEnhancedReducer = (reducer, initState, initializer) => {
  const lastState = useRef(initState)
  const getState = useCallback(() => lastState.current, [])
  return [
    ...useReducer(
      (state, action) => lastState.current = reducer(state, action),
      initState,
      initializer,
    ),
    getState,
  ]
}

const reduce = (state, action) => {
  switch (action.type) {
    case actions.SET_FORM_REF:
      return {
        ...state,
        formRef: action.payload,
      }
    case actions.REFRESH_IFRAME:
      return {
        ...state,
        refreshing: true,
      }
    case actions.FINISHED_REFRESHING_IFRAME:
      return {
        ...state,
        refreshing: false,
      }
    case actions.TOGGLE_THEME_EDITOR:
      return {
        ...state,
        themeEditor: true,
      }
    case actions.TOGGLE_PAGE_EDITOR:
      return {
        ...state,
        themeEditor: false,
      }
    case actions.SET_HTML:
      return {
        ...state,
        html: action.payload,
        refreshing: false,
      }
    case actions.UPDATE_DEV_MODE:
      return {
        ...state,
        admin: {
          ...state.admin,
          devMode: action.payload,
        },
      }
    case actions.SET_SAVING:
      return {
        ...state,
        saving: action.payload,
      }
    case actions.SET_IFRAME_REF:
      return {
        ...state,
        iframeRef: action.payload,
      }
    case actions.SHOW_CONTENT_EDITOR:
      return {
        ...state,
        view: 'content',
      }
    case actions.SHOW_THEME_EDITOR:
      return {
        ...state,
        view: 'theme',
      }
    case actions.SHOW_PREVIEW:
      return {
        ...state,
        view: 'preview',
      }
    case actions.SET_BLOCKS:
      return {
        ...state,
        blocks: action.payload,
      }
    case actions.SET_PUBLISHING:
      return {
        ...state,
        publishing: action.payload,
      }
    case actions.SET_ID:
      return {
        ...state,
        id: action.payload,
      }
    case actions.SET_PAGE:
      return {
        ...state,
        page: action.payload,
      }
    case actions.SET_PUBLISH_URLS:
      return {
        ...state,
        publishUrl: action.payload.publishUrl,
        unpublishUrl: action.payload.unpublishUrl,
      }
    case actions.SET_UNPUBLISHING:
      return {
        ...state,
        unpublishing: action.payload,
      }
    case actions.UPDATE_FIELD:
      return {
        ...state,
        fields: updateFields({ fields: state.fields, key: action.payload.key, value: action.payload.value })
      }
    case actions.TOGGLE_MOBILE_SIDEBAR:
      return {
        ...state,
        mobileSidebar: !state.mobileSidebar,
      }
    default:
      return state
  }
}

const StateProvider = (props) => {
  const initialState = {
    ...props,
    mobileSidebar: true,
    themeEditor: props.themeOnly,
    formRef: null,
    iframeRef: null,
    refreshing: false,
    saving: false,
    view: 'content',
    html: '',
    publishing: false,
    unpublishing: false,
  }

  const [state, dispatch, getState] = useEnhancedReducer(reduce, initialState)

  const asyncDispatch = (action) => {
    const state = getState()
    switch (action.type) {
      case actions.GET_BLOCKS_DATA:
        return getBlocksData(state, dispatch)
      case actions.UPDATE_HTML:
        updatePreviewHtml(state, action, dispatch)
        return dispatch({ type: actions.REFRESH_IFRAME })
      case actions.SAVE_PAGE:
        savePage(state, dispatch, true)
        return dispatch({ type: actions.SET_SAVING, payload: true })
      case actions.START_PUBLISHING:
        saveForPublishing(state, dispatch)
        return dispatch({ type: actions.SET_PUBLISHING, payload: 'saving'})
      case actions.PUBLISH_PAGE:
        console.log('should be publishing')
        publishPage(state, dispatch)
        return dispatch({ type: actions.SET_PUBLISHING, payload: 'publishing'})
      case actions.UNPUBLISH_PAGE:
        unpublishPage(state, dispatch)
        return dispatch({ type: actions.SET_UNPUBLISHING, payload: true})
      default:
        return dispatch(action)
    }
  }

  const usableData = {
    ...state,
    viewingPreview: state.view === 'preview',
    viewingContentEditor: state.view === 'content',
    viewingThemeEditor: state.view === 'theme',
    actions,
  }

  const value = {
    ...usableData,
    dispatch: asyncDispatch.bind(usableData)
  }

  return <Provider value={value}>{props.children}</Provider>
}

export { store, StateProvider, actions }
